// ============================================================
// SideQuests - Runtime SEO head manager
//
// Applies the route-specific metadata registry to document.head on
// every client-side navigation so hydrated metadata always matches
// the statically generated HTML for that route.
//
// It NEVER falls back to homepage defaults: an unknown route uses
// the noindex Not Found metadata and drops the canonical link.
// ============================================================

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  resolveRouteSeo,
  normalizePath,
  NOT_FOUND_SEO,
  SITE_ORIGIN,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  type RouteSeo,
} from './routeSeo';
import { getMunicipality } from '../data/municipalities';

const META_DESCRIPTIONS = ['name:description'];

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeCanonical(): void {
  const el = document.head.querySelector('link[rel="canonical"]');
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

function canonicalHref(meta: RouteSeo): string {
  if (meta.path === '/') return SITE_ORIGIN + '/';
  return SITE_ORIGIN + meta.path;
}

/**
 * Apply a resolved RouteSeo object to the live document head.
 *
 * `currentPath` is the route the browser is actually on. It is used only
 * for the og:url of a noindex page whose metadata carries no path of its
 * own (the Not Found fallback), so og:url describes the requested URL
 * instead of silently claiming the homepage.
 */
export function applySeoHead(meta: RouteSeo, currentPath?: string): void {
  if (typeof document === 'undefined') return;

  document.title = meta.title;

  upsertMeta('name', 'description', meta.description);
  upsertMeta('name', 'robots', meta.robots);

  upsertMeta('property', 'og:type', meta.ogType);
  upsertMeta('property', 'og:site_name', 'SideQuests');
  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);
  upsertMeta('property', 'og:image', meta.image);
  upsertMeta('property', 'og:image:width', String(OG_IMAGE_WIDTH));
  upsertMeta('property', 'og:image:height', String(OG_IMAGE_HEIGHT));
  upsertMeta('property', 'og:image:alt', meta.imageAlt);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
  upsertMeta('name', 'twitter:image', meta.image);
  upsertMeta('name', 'twitter:image:alt', meta.imageAlt);

  if (meta.selfCanonical) {
    upsertCanonical(canonicalHref(meta));
    upsertMeta('property', 'og:url', canonicalHref(meta));
  } else {
    removeCanonical();
    // Never inherit the homepage URL. A noindex route knows its own path;
    // the Not Found fallback does not, so the live pathname is used.
    const ogPath = meta.path || normalizePath(currentPath || '/');
    upsertMeta('property', 'og:url', SITE_ORIGIN + (ogPath === '/' ? '/' : ogPath));
  }

  void META_DESCRIPTIONS;
}

function lookupTown(slug: string): { name: string; region: string } | undefined {
  const m = getMunicipality(slug);
  if (!m) return undefined;
  return { name: m.name, region: m.region };
}

/**
 * Keep document.head in sync with the current route. Call once from the
 * app shell. Resets on every navigation so metadata from a previously
 * viewed route is never retained.
 */
export function useSeoHead(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const resolved = resolveRouteSeo(pathname, lookupTown);
    applySeoHead(resolved ?? NOT_FOUND_SEO, pathname);
  }, [pathname]);
}
